import BaseView from "./BaseView";
import SceneView from "./SceneView";
import ViewManager from "../utils/ViewManager";
import ReelSymbol from "./Symbol";
import SymbolAnimation from "./SymbolAnimation";
import { IObjectConfig } from "../baseConfigs/layoutConfigShema";
import Layout from "../utils/Layout";
import { ReelOrientation } from "./Reel";
import { IWinData, IOverlayData } from "../server/data/ISpinData";
import { Ease } from "../Ease";
import { Timeline } from "../Timeline";
import InstanceFactory from "../factories/InstanceFactory";
import PromiseHelpers from "../utils/PromiseHelpers";
import SpinModel from "../model/SpinModel";
import GameModel from "../model/GameModel";
import { Container } from "pixi.js";
import Configs from "../../../src/vendorConfigs/Configs";

export default class WinSymbolsView extends BaseView {
    protected dataScene: SceneView;
    protected scene: SceneView;
    protected winSymbolsLayer: string;
    protected winSymbolsContainer: Container;
    protected reelExtraSymbols: number;
    protected reelSymMap: Map<string, SymbolAnimation>;
    protected symbolsConfig: {
        positionDefinitions?: Map<string, IObjectConfig>;
        rowSeparator?: IObjectConfig;
        reelBounds?: IObjectConfig;
        reelSeparator?: IObjectConfig;
    };
    constructor() {
        super();
    }

    public getSymBound(reelIndex?: number): IObjectConfig {
        let result = this.symbolsConfig.positionDefinitions.get("sym");
        if (reelIndex && this.symbolsConfig.positionDefinitions.has(`sym_${reelIndex}`)) {
            result = this.symbolsConfig.positionDefinitions.get(`sym_${reelIndex}`)
        }

        return result
    }

    public getSymbolContainer() {
        return this.winSymbolsContainer
    }

    public create(
        scene: SceneView,
        extraSymbols: number,
        winSymbolsLayer?: string
    ) {
        this.dataScene = scene;
        this.reelExtraSymbols = extraSymbols;
        this.winSymbolsLayer = winSymbolsLayer;
        this.winSymbolsContainer = new Container();
        this.addChild(this.winSymbolsContainer);
        if (winSymbolsLayer) {
            let sceneName = "";
            if (scene.name.indexOf("-") != -1) {
                let splitName = scene.name.split("-");
                sceneName = splitName[splitName.length - 1];
            }
            this.scene = ViewManager.getLayer(winSymbolsLayer).setScene(
                sceneName
            );
        } else {
            this.scene = scene;
        }
        if (this.scene.containers.has("wins")) {
            this.scene.addSubView(this, "wins", "wins");
            this.scene.containers.get("wins").disableMask();
        } else {
            this.scene.addSubView(this, "wins", "reels");
            // this.scene.containers.get("wins").dissableMask();
        }
        this.symbolsConfig = {};
        this.symbolsConfig.positionDefinitions = this.dataScene.getPositionDefinitions();
        this.symbolsConfig.reelSeparator = this.symbolsConfig.positionDefinitions.get(
            "reel_border"
        );
        this.symbolsConfig.rowSeparator = this.symbolsConfig.positionDefinitions.get(
            "row_border"
        );
        this.symbolsConfig.rowSeparator = this.symbolsConfig.positionDefinitions.get(
            "row_border"
        );
    }

    public hideWinlineWinSymbols(winLineData: IWinData) {
        const winSyms = this.getWinlineWinSymbols(winLineData);
        winSyms.forEach((symbol) => symbol.hideWin());
    }

    public clearWins = () => {
        this.reelSymMap?.forEach(s => s.hideWin())
    }

    public async clearOverlays() {
        if (this.reelSymMap) {
            const values = Array.from(this.reelSymMap.values());
            for (const anim of values) {
                await anim.removeOverlay();
            }
        }
    }

    public playWinLineSymbols(
        winLineData: IWinData,
        ids: number[]
    ): Promise<boolean> {
        const winSyms = this.getWinlineWinSymbols(winLineData);
        let promises: Promise<boolean>[] = [];
        // winSyms.forEach(winSym=>{
        //     winSym.showWin()
        // })
        //TODO FIX NO ANIM ISSUE
        promises.push(PromiseHelpers.tweenPromise({time:0},GameModel.gameConfig.minAnimaTime || 0.5,{time:100}));
        for (let winSymId = 0; winSymId < winSyms.length; winSymId++) {
            promises.push(winSyms[winSymId].showWin(ids[winSymId]));
        }
        return PromiseHelpers.combineParallel(promises);
    }

    public getWinlineWinSymbols(winLineData: IWinData): SymbolAnimation[] {
        const winningReels = winLineData.winReels;
        const winningPositions = winLineData.winSymbols;

        const winSymbols: SymbolAnimation[] = [];

        for (
            let winReelIdx = 0;
            winReelIdx < winningReels.length;
            winReelIdx++
        ) {
            const reelIdx = winningReels[winReelIdx];
            let symIdx;
            let sym;
            if (SpinModel.bonusWin) {
                if (
                    reelIdx >= SpinModel.firstWildIndex &&
                    reelIdx <=
                        SpinModel.firstWildIndex + SpinModel.wildVersion - 1
                ) {
                    sym = this.getAnimAtReelCell(SpinModel.firstWildIndex, 0);
                    if (winSymbols.indexOf(sym) == -1) {
                        winSymbols.push(sym);
                    }
                } else {
                    symIdx = winningPositions[winReelIdx];
                    sym = this.getAnimAtReelCell(reelIdx, symIdx);
                    winSymbols.push(sym);
                }
            } else {
                symIdx = winningPositions[winReelIdx];
                sym = this.getAnimAtReelCell(reelIdx, symIdx);
                winSymbols.push(sym);
            }
        }

        return winSymbols;
    }

    public stopAll() {
        if (!this.reelSymMap) {
            return;
        }
        this.reelSymMap.forEach((anim) => {
            if (!anim.isLocked) {
                anim.stopAnimation();
            }
        });
    }

    public visibleCount(symId?: number): number {
        let count = 0;
        if (this.reelSymMap) {
            this.reelSymMap.forEach((anim) => {
                if (anim.visible && anim.isOn() && (isNaN(symId) || symId === anim.id )) {
                    count++;
                }
            });
        }
        return count;
    }

    public moveSymbols(
        overlay: IOverlayData,
        moveAnim?: string
    ): Promise<boolean> {
        let promisesList = [];
        if (this.reelSymMap && overlay && overlay.path && overlay.path.length) {
            this.reelSymMap.forEach((anim, key) => {

                let coords = this.splitReelKey(key);
                let overlaySym = overlay.symbolPosition[coords.reel][coords.row];
                if (anim.isLocked && this.config.symbolIds.indexOf(overlaySym)!=-1 && overlay.path) {
                    anim.parent.addChild(anim);
                    let result = overlay.path[overlay.path.length - 1];
                    let resultCoords = {
                        reel:result.reel,
                        row:result.row,
                        resultSym:overlaySym
                    }

                    let promise = new Promise(res => {
                        let tl = new Timeline({
                            paused: true, onComplete: () => {
                                res();
                                this.onWalkingEnd(resultCoords);
                            }
                        });
                        let duration = (moveAnim
                        ? anim.getAnimDuration(
                              resultCoords.resultSym,
                              moveAnim
                          )
                        : this.config.symbolWalkDur || 0)/overlay.path.length;
                        overlay.path.forEach((coords) => {
                            let position = this.getPositionAt(
                                coords.reel,
                                coords.row
                            );

                            tl.to(anim,duration,{
                                x: position.x,
                                y: position.y,
                                ease:Ease.None
                            })

                        });
                        tl.play();

                    })
                    promisesList.push(promise);

                }
            });
        }
        return PromiseHelpers.combineParallel(promisesList);
    }

    public walkSymbols(
        overlay: IOverlayData,
        direction: WalkDirection,
        walkAnim?: string
    ): Promise<boolean> {
        let counter = 0;
        let promisesList = [];
        if (this.reelSymMap && overlay) {
            this.reelSymMap.forEach((anim, key) => {
                if (anim.isLocked) {
                    let resultCoords = this.getWalkCoords(
                        direction,
                        overlay,
                        key
                    );

                    if (resultCoords) {
                        anim.isLocked = false;
                        counter++;
                        let duration = walkAnim
                            ? anim.getAnimDuration(
                                  resultCoords.resultSym,
                                  walkAnim
                              )
                            : this.config.symbolWalkDur || 0;
                        let position = this.getPositionAt(
                            resultCoords.reel,
                            resultCoords.row
                        );
                        if (walkAnim) {
                            promisesList.push(
                                anim.showAnim(resultCoords.resultSym, walkAnim)
                            );
                        } else {
                            promisesList.push(
                                anim.showStatic(resultCoords.resultSym)
                            );
                        }
                        promisesList.push(
                            PromiseHelpers.tweenPromise(anim, duration, {
                                x: position.x,
                                y: position.y,
                            }).then(()=>{
                                this.onWalkingEnd(resultCoords);
                            })
                        );
                    }
                }
            });
        } else {
            // this.unlockAll();
        }
        return PromiseHelpers.combineParallel(promisesList);
    }

    protected onWalkingEnd(resultCoords:{reel:number,row:number,resultSym:number}) {
        if(this.hasAnimAtReelCell(resultCoords.reel,resultCoords.row)) {
            let afterAnim = this.getAnimAtReelCell(resultCoords.reel,resultCoords.row);
            afterAnim.showStatic(resultCoords.resultSym);

        }
    }

    protected getWalkCoords(
        direction: WalkDirection,
        overlay: IOverlayData,
        key: string
    ): { reel: number; row: number; resultSym: number } {
        let resultCoords: { reel: number; row: number; resultSym };
        let resultSym: number;
        let coords = this.splitReelKey(key);
        let symId = coords.row;
        switch (direction) {
            case WalkDirection.UP:
                resultSym = overlay.symbolPosition[coords.reel][coords.row];
                if (this.config.symbolIds.indexOf(resultSym) >= 0) {
                    resultCoords = {
                        reel: coords.reel,
                        row: coords.row - 1,
                        resultSym,
                    };
                }
                break;
            case WalkDirection.UP_LEFT:
                resultSym = overlay.symbolPosition[coords.reel][coords.row];

                if (this.config.symbolIds.indexOf(resultSym) >= 0) {
                    resultCoords = {
                        reel: overlay.symbolPosition[coords.reel - 1] ? coords.reel - 1 : coords.reel,
                        row: coords.row - 1,
                        resultSym,
                    };
                }
                break;
            case WalkDirection.UP_RIGHT:
                resultSym =overlay.symbolPosition[coords.reel ][coords.row];

                if (this.config.symbolIds.indexOf(resultSym) >= 0) {
                    resultCoords = {
                        reel:overlay.symbolPosition[coords.reel + 1] ? coords.reel + 1 : coords.reel,
                        row: coords.row - 1,
                        resultSym,
                    };
                }
                break;
            case WalkDirection.Down_RIGHT:
                resultSym = overlay.symbolPosition[coords.reel][coords.row];

                if (this.config.symbolIds.indexOf(resultSym) >= 0) {
                    resultCoords = {
                        reel:overlay.symbolPosition[coords.reel + 1] ? coords.reel + 1 : coords.reel,
                        row: coords.row + 1,
                        resultSym,
                    };
                }
                break;
            case WalkDirection.Down_LEFT:
                resultSym = overlay.symbolPosition[coords.reel][coords.row];

                if (this.config.symbolIds.indexOf(resultSym) >= 0) {
                    resultCoords = {
                        reel: overlay.symbolPosition[coords.reel - 1] ? coords.reel - 1 : coords.reel,
                        row: coords.row + 1,
                        resultSym,
                    };
                }
                break;
            case WalkDirection.Down:
                resultSym = overlay.symbolPosition[coords.reel][coords.row];
                if (this.config.symbolIds.indexOf(resultSym) >= 0) {
                    resultCoords = {
                        reel: coords.reel,
                        row: coords.row + 1,
                        resultSym,
                    };
                }
                break;
            case WalkDirection.Left:
                resultSym =overlay.symbolPosition[coords.reel ][symId];
                if (this.config.symbolIds.indexOf(resultSym) >= 0) {
                    if(overlay.symbolPosition[coords.reel - 1]) {
                        resultCoords = {
                            reel: coords.reel - 1,
                            row: coords.row,
                            resultSym,
                        };
                    } else {
                        resultCoords = {
                            reel: coords.reel,
                            row: coords.row,
                            resultSym,
                        };
                    }
                }
                break;
            case WalkDirection.Right:
                resultSym = overlay.symbolPosition[coords.reel][symId];
                if (this.config.symbolIds.indexOf(resultSym) >= 0) {
                    if(overlay.symbolPosition[coords.reel + 1]) {
                        resultCoords = {
                            reel: coords.reel + 1,
                            row: coords.row,
                            resultSym,
                        };
                    } else {
                        resultCoords = {
                            reel: coords.reel,
                            row: coords.row,
                            resultSym,
                        };
                    }
                }
                break;
        }

        return resultCoords;
    }

    public lockSymbols(
        overlay: IOverlayData,
        lockAnim?: string
    ): Promise<boolean> {
        return new Promise((res) => {
            let counter = 0;
            overlay.symbolPosition.forEach((reel, reelId) => {
                reel.forEach((sym, rowId) => {
                    let symId = rowId;
                    if (this.config.symbolIds.indexOf(sym) >= 0) {
                        let anim = this.getAnimAtReelCell(reelId, symId);
                        counter++;
                        if (lockAnim) {
                            anim.showAnim(sym, lockAnim).then(() => {
                                counter--;
                                if (counter <= 0) {
                                    res();
                                }
                            });
                        } else {
                            counter--;
                            anim.showStatic(sym);
                        }
                        anim.isLocked = true;
                    } else if (
                        this.reelSymMap &&
                        this.reelSymMap.has(this.generateReelKey(reelId, symId))
                    ) {
                        let animToUnlock = this.reelSymMap.get(
                            this.generateReelKey(reelId, symId)
                        );
                        animToUnlock.isLocked = false;
                        this.returnToPosition(reelId, rowId);
                    }

                    if (counter <= 0) {
                        res();
                    }
                });
            });
        });
    }

    public unlockAll(isException?: (anim: SymbolAnimation, reelIdx?: {reel:number, row: number}) => boolean) {
        if (this.reelSymMap) {
            this.reelSymMap.forEach((anim, key) => {
                if (
                    !isException ||
                    !isException(anim, this.splitReelKey(key))
                ) {
                    anim.isLocked = false;
                    anim.isWalking = false;
                }
            });
        }
    }

    playAnimaAtReelCell(
        reel: number,
        row: number,
        id: number,
        anim?: string,
        timeScale?: number
    ): Promise<boolean> {
        let symAnim = this.getAnimAtReelCell(reel, row);
        if (!symAnim.isLocked) {
            return symAnim.showAnim(id, anim, false, false, null, null, timeScale);
        } else {
            return new Promise((res) => {
                res(true);
            });
        }
    }

    public hasAnimAtReelCell(reel: number, row: number): boolean {
        if (!this.reelSymMap) {
            return false;
        }
        let key = this.generateReelKey(reel, row);
        return this.reelSymMap.has(key);
    }

    public getAnimAtReelCell(reel: number, row: number): SymbolAnimation {
        if (!this.reelSymMap) {
            this.reelSymMap = new Map<string, SymbolAnimation>();
        }

        let key = this.generateReelKey(reel, row);
        if (!this.reelSymMap.has(key)) {
            this.reelSymMap.set(key, this.createSymbolAnimation(reel));
            let anim = this.reelSymMap.get(key);
            if(this.winSymbolsContainer) {

                this.winSymbolsContainer.addChild(anim);
            } else {

                this.addChild(anim);
            }
            this.returnToPosition(reel, row);
        } else {
            this.returnToPosition(reel, row);
        }

        return this.reelSymMap.get(key);
    }

    getSymbolAnim(reel: number, row: number): SymbolAnimation {
        return this.reelSymMap?.get(this.generateReelKey(reel, row))
    }

    returnToPosition(reel: number, row: number) {
        let key = this.generateReelKey(reel, row);
        if (this.reelSymMap.has(key)) {
            let anim = this.reelSymMap.get(key);
            let position = this.getPositionAt(reel, row);
            anim.x = position.x;
            anim.y = position.y;
        }
    }

    getPositionAt(reel: number, row: number): { x: number; y: number } {
        const reelBounds = this.symbolsConfig.positionDefinitions.get(
            "reel_" + reel
        );
        const symBounds = this.getSymBound(reel);
        let orientation =
            reelBounds.width <= reelBounds.height
                ? ReelOrientation.Vertical
                : ReelOrientation.Horizontal;
        const xOrigin = Layout.scalePixels(
            // Horizontal origin should be the middle of the reel, not the symbol
            reelBounds.x + reelBounds.width * 0.5
        );
        const yOrigin = Layout.scalePixels(
            reelBounds.y + symBounds.height * 0.5
        );
        const offsetX = this.symbolsConfig.reelSeparator
            ? this.symbolsConfig.reelSeparator.width
            : 0;
        const offsetY = this.symbolsConfig.rowSeparator
            ? this.symbolsConfig.rowSeparator.height
            : 0;

            // export enum ReelOrientation {
            //     Horizontal = 0,
            //     Vertical = 1,
            // }
        let x =
            xOrigin +
            Layout.scalePixels(
                (symBounds.width + offsetX) *
                    (row * (1 - orientation))
            );
        let y =
            yOrigin +
            Layout.scalePixels(
                (symBounds.height + offsetY) *
                    (row * orientation)
            );
        return {
            x,
            y,
        };
    }

    public createSymbolAnimation(reel: number): SymbolAnimation {
        let anim = InstanceFactory.getClass(
            "SymbolAnimation"
        ) as SymbolAnimation;

        const symBounds = this.getSymBound(reel);

        if(Configs.game.symbolType.spine) {
            let spineReelSym = InstanceFactory.getClass(
                "Symbol"
            ) as ReelSymbol;

            spineReelSym.create(
                symBounds,
                this.symbolsConfig.positionDefinitions
            );

            anim.create(
                symBounds,
                this.symbolsConfig.positionDefinitions,
                spineReelSym
            );
        } else {
            anim.create(
                symBounds,
                this.symbolsConfig.positionDefinitions
            );
        }

        return anim;
    }

    protected generateReelKey(reel: number, row: number): string {
        return reel + "x" + row;
    }

    protected splitReelKey(key: string): { reel: number; row: number } {
        let splitKey = key.split("x");
        return {
            reel: parseInt(splitKey[0]),
            row: parseInt(splitKey[1]),
        };
    }

    getChildren() {
        if(this.winSymbolsContainer) {
            return this.winSymbolsContainer.children;
        }

        return this.children;
    }
}

export enum WalkDirection {
    UP = "up",
    UP_LEFT = "up_left",
    UP_RIGHT = "up_right",
    Down = "down",
    Down_LEFT = "down_left",
    Down_RIGHT = "down_right",
    Left = "left",
    Right = "right",
}
